import { config } from '@/config/env'
import { ApiError, appendQuery, currentToken, readResponse, type QueryValue } from '@/api/http'
import type { ZodType } from 'zod'

/*
 * Die Fehlertypen und der Token-Provider liegen in http.ts, weil PostgREST
 * dieselben braucht. Sie werden hier weitergereicht, damit die vielen
 * bestehenden Importe aus '@/api/client' unverändert bleiben.
 */
export { ApiContractError, ApiError, setTokenProvider, type TokenProvider } from '@/api/http'

interface RequestOptions<T> {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  readonly query?: Readonly<Record<string, QueryValue>>
  readonly body?: unknown
  readonly signal?: AbortSignal
  readonly headers?: Readonly<Record<string, string>>
  readonly allowNotModified?: boolean
  /** Prüft erfolgreiche JSON-Antworten, bevor sie Anwendungscode erreichen. */
  readonly schema?: ZodType<T>
}

export interface ApiResponse<T> {
  readonly data: T
  readonly status: number
  readonly headers: Headers
}

export interface ApiNotModifiedResponse {
  readonly data: undefined
  readonly status: 304
  readonly headers: Headers
}

export type ApiResponseResult<T> = ApiResponse<T> | ApiNotModifiedResponse

function buildUrl(path: string, query: RequestOptions<unknown>['query']): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = `${config.apiBaseUrl}${normalizedPath}`

  return query ? appendQuery(url, query) : url
}

/**
 * Ruft die Weltmaschine-API auf.
 *
 * Das Token geht als Bearer-Header raus. Das ist nicht optional: Die
 * Weltmaschine leitet alles außer der Billbee-Route auf das JwtBearer-
 * Schema, ein Cookie hilft hier nicht.
 */
export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions<T> = {},
): Promise<T> {
  const result = await requestWithMetadata(path, options)
  if (result.status === 304) {
    throw new ApiError(304, buildUrl(path, options.query), '')
  }
  return result.data as T
}

/** Ruft einen Endpunkt auf und reicht Status sowie Header an den Fachbereich weiter. */
export async function apiRequestWithMetadata<T = unknown>(
  path: string,
  options: RequestOptions<T> = {},
): Promise<ApiResponseResult<T>> {
  return requestWithMetadata(path, { ...options, allowNotModified: true })
}

async function requestWithMetadata<T>(
  path: string,
  options: RequestOptions<T>,
): Promise<ApiResponseResult<T>> {
  const url = buildUrl(path, options.query)
  const token = currentToken()

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers,
  }
  if (token !== undefined) {
    headers.Authorization = `Bearer ${token}`
  }
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  const requestInit: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  }

  const response = await fetch(url, requestInit)

  if (response.status === 304 && options.allowNotModified === true) {
    return { data: undefined, status: 304, headers: response.headers }
  }

  const data = await readResponse<T>(
    response,
    url,
    options.schema ? { schema: options.schema } : {},
  )
  return { data, status: response.status, headers: response.headers }
}
